import React, { useEffect, useState } from "react";
import "./AnimatedPhotoWall.css";
import "./NoScroll.css";
import { API_ENDPOINTS, apiRequest } from "../config/api";
import { getVideoUrl, getImageUrl } from "../config/assets";
import FloatingPhotoBubble from "./FloatingPhotoBubble";
import FloatingPhotoCircle from "./FloatingPhotoCircle";
import FloatingAcheBalloon from "./FloatingAcheBalloon";

type Person = {
  id: string;
  external_id: string;
  name: string;
  photo_url: string;
  field_1?: string;  // Campo genérico 1
  field_2?: string;  // Campo genérico 2
  field_3?: string;  // Campo genérico 3
};

const AnimatedPhotoWall: React.FC = () => {
  const [people, setPeople] = useState<Person[]>([]);
  
    // Estado para as bolhas de foto com transições graduais
  const [photoBubbles, setPhotoBubbles] = useState<Array<{
    person: Person;
    isNew: boolean;
    shouldEnter?: boolean;
    shouldExit?: boolean;
  }>>([]);
  
  // Estado para os balões ACHE com transições graduais
  const [acheBalloons, setAcheBalloons] = useState<Array<{
    id: string;
    sizePercentage: number;
    speed: number;
    shouldEnter?: boolean; // Flag para indicar entrada suave
    shouldExit?: boolean; // Flag para indicar que deve sair
  }>>([]);
  
  const [balloonIdCounter, setBalloonIdCounter] = useState(0);

  // Aplicar estilos globais quando o componente é montado
  useEffect(() => {
    // Garantir que não há scroll na aplicação
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100vw';
    document.body.style.height = '100vh';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.position = 'fixed';
    document.documentElement.style.width = '100vw';
    document.documentElement.style.height = '100vh';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';

    // Cleanup quando o componente é desmontado
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      
      document.documentElement.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.width = '';
      document.documentElement.style.height = '';
      document.documentElement.style.margin = '';
      document.documentElement.style.padding = '';
    };
  }, []);

  useEffect(() => {
    let isFirstLoad = true;
    
    const fetchPhotos = async () => {
      try {
        const res = await apiRequest(API_ENDPOINTS.REGISTRATION_PHOTOS);
        const data: { new: Person[], old: Person[] } = await res.json();
        const new_data = data.new;
        const old_data = data.old;
        
        if (isFirstLoad) {
          // Primeira carga: inicializar todas as foto bubbles
          const newBubbles = new_data.map(person => ({
            person,
            isNew: true,
            shouldEnter: true
          }));
          const oldBubbles = old_data.map(person => ({
            person,
            isNew: false,
            shouldEnter: true
          }));
          setPhotoBubbles([...newBubbles, ...oldBubbles]);
          isFirstLoad = false; 
        } else {
          // Atualizações subsequentes: gerenciar transições
          setPhotoBubbles(currentBubbles => {
            const currentPeople = currentBubbles.map(b => b.person);
            const allNewPeople = [...new_data, ...old_data];
            
            // Identificar pessoas que devem sair (não estão na nova lista)
            const peopleToExit = currentPeople.filter(
              currentPerson => !allNewPeople.find(newPerson => newPerson.id === currentPerson.id)
            );
            
            // Identificar pessoas que devem entrar (estão na nova lista mas não na atual)
            const peopleToEnter = allNewPeople.filter(
              newPerson => !currentPeople.find(currentPerson => currentPerson.id === newPerson.id)
            );
            
            // Atualizar estado: marcar para sair + adicionar novos
            const updatedBubbles = [
              // Bolhas existentes (algumas marcadas para sair)
              ...currentBubbles.map(bubble => ({
                ...bubble,
                shouldExit: peopleToExit.some(p => p.id === bubble.person.id)
              })),
              // Novas bolhas marcadas para entrar
              ...peopleToEnter.map(person => ({
                person,
                isNew: new_data.find(p => p.id === person.id) ? true : false,
                shouldEnter: true
              }))
            ];
            
            return updatedBubbles;
          });
        }
        
        
        setPeople([...new_data, ...old_data]);
      } catch (err) {
        console.error('Erro ao buscar fotos:', err);
      }
    };
    
    fetchPhotos();
    const fetchInterval = setInterval(fetchPhotos, 10000);
    return () => clearInterval(fetchInterval);
  }, []);

  // useEffect removido pois foi integrado ao fetchPhotos

  // useEffect para gerar balões iniciais e gerenciar transições
  useEffect(() => {
    let localCounter = balloonIdCounter;
    
    // Gerar balões iniciais
    const generateInitialBalloons = () => {
      const balloonCount = Math.floor(Math.random() * 3) + 3; // 3-5 balões
      const balloons = [];
      
      for (let i = 0; i < balloonCount; i++) {
        balloons.push({
          id: `ache-balloon-${localCounter + i}`,
          sizePercentage: Math.random() * 20 + 15, // 15-35%
          speed: Math.random() * 20 + 20, // 20-40 pixels/segundo
          shouldEnter: true // Entrar suavemente
        });
      }
      
      localCounter += balloonCount;
      setAcheBalloons(balloons);
      setBalloonIdCounter(localCounter);
    };

    // Gerenciar transições de balões
    const manageBalloonsTransition = () => {
      const targetCount = Math.floor(Math.random() * 3) + 3; // 3-5 balões

      setAcheBalloons(currentBalloons => {
        const activeBalloons = currentBalloons.filter(b => !b.shouldExit);
        const currentCount = activeBalloons.length;
        
        if (targetCount > currentCount) {
          // Adicionar novos balões
          const newBalloons = [];
          const balloonsToAdd = targetCount - currentCount;
          
          for (let i = 0; i < balloonsToAdd; i++) {
            newBalloons.push({
              id: `ache-balloon-${localCounter}`,
              sizePercentage: Math.random() * 20 + 15,
              speed: Math.random() * 20 + 20,
              shouldEnter: true
            });
            localCounter++;
          }
          
          setBalloonIdCounter(localCounter);
          return [...currentBalloons, ...newBalloons];
          
        } else if (targetCount < currentCount) {
          // Marcar balões para sair
          const balloonsToRemove = currentCount - targetCount;
          const shuffledActive = [...activeBalloons].sort(() => Math.random() - 0.5);
          const balloonsToExit = shuffledActive.slice(0, balloonsToRemove);
          
          return currentBalloons.map(balloon => 
            balloonsToExit.find(b => b.id === balloon.id)
              ? { ...balloon, shouldExit: true }
              : balloon
          );
        }
        
        return currentBalloons;
      });
    };

    // Gerar balões iniciais imediatamente
    generateInitialBalloons();
    
    // Configurar transições a cada 25 segundos
    const interval = setInterval(manageBalloonsTransition, 25000);
    
    return () => {
      clearInterval(interval);
    };
  }, []); // Sem dependências para evitar loops

  // Callback quando um balão completa sua saída
  const handleBalloonExitComplete = (balloonId: string) => {
    setAcheBalloons(prev => {
      const updatedBalloons = prev.filter(balloon => balloon.id !== balloonId);
      return updatedBalloons;
    });
  };

  // Callback quando uma foto bubble completa sua saída
  const handlePhotoBubbleExitComplete = (personId: string) => {
    setPhotoBubbles(prev => {
      const updatedBubbles = prev.filter(bubble => bubble.person.id !== personId);
      return updatedBubbles;
    });
  };

  // Definir áreas de movimento evitando as logos e o texto central
  const getMovementBounds = () => {
    // Usar tamanho máximo possível para cálculo das áreas (20% da altura da tela)
    const maxBubbleSize = Math.round((20 / 100) * window.innerHeight);
    const padding = 50; // Espaço extra ao redor das logos e texto
    
    // Calcular área do logo ASCME (canto superior esquerdo)
    // Baseado no CSS: top: 4%, left: 1%, height: 11vh
    const logoAscmeHeight = window.innerHeight * 0.11; // 11vh
    const logoAscmeWidth = logoAscmeHeight * (16/9); // Estimativa de proporção
    const logoAscmeArea = {
      left: window.innerWidth * 0.01, // left: 1%
      right: window.innerWidth * 0.01 + logoAscmeWidth,
      top: window.innerHeight * 0.04, // top: 4%
      bottom: window.innerHeight * 0.04 + logoAscmeHeight
    };
    
    // Calcular área do logo ACHE (canto superior direito)
    // Baseado no CSS: top: 3.5%, right: 1%, height: 9vh
    const logoAcheHeight = window.innerHeight * 0.09; // 9vh
    const logoAcheWidth = logoAcheHeight * 2; // Estimativa de proporção
    const logoAcheArea = {
      left: window.innerWidth - (window.innerWidth * 0.01) - logoAcheWidth, // right: 1%
      right: window.innerWidth - (window.innerWidth * 0.01),
      top: window.innerHeight * 0.035, // top: 3.5%
      bottom: window.innerHeight * 0.035 + logoAcheHeight
    };
    
    // Calcular área do texto central
    // Baseado no CSS: posição centralizada, font-size: 20vh, max-width: 80vw
    const textHeight = window.innerHeight * 0.20; // 20vh (font-size)
    const textWidth = Math.min(window.innerWidth * 0.80, window.innerWidth * 0.6); // Estimativa de largura real
    const textArea = {
      left: (window.innerWidth - textWidth) / 2,
      right: (window.innerWidth + textWidth) / 2,
      top: (window.innerHeight - textHeight) / 2,
      bottom: (window.innerHeight + textHeight) / 2
    };
    
    return {
      minX: 0,
      maxX: window.innerWidth - maxBubbleSize,
      minY: 0,
      maxY: window.innerHeight - maxBubbleSize,
      // Área proibida (logo ASCME canto superior esquerdo + padding)
      forbiddenArea: {
        left: logoAscmeArea.left - padding,
        right: logoAscmeArea.right + padding,
        top: logoAscmeArea.top - padding,
        bottom: logoAscmeArea.bottom + padding
      },
      // Área adicional proibida (logo ACHE canto superior direito + padding)
      forbiddenAreaAche: {
        left: logoAcheArea.left - padding,
        right: logoAcheArea.right + padding,
        top: logoAcheArea.top - padding,
        bottom: logoAcheArea.bottom + padding
      },
      // Área proibida do texto central
      forbiddenAreaText: {
        left: textArea.left - padding,
        right: textArea.right + padding,
        top: textArea.top - padding,
        bottom: textArea.bottom + padding
      }
    };
  };

  return (
    <div className="animated-bg" style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      overflow: "hidden",
      margin: 0,
      padding: 0
    }}>
      <video autoPlay loop muted
        className="background-video"
        src={getVideoUrl('Fundo_05_MP4.mp4')}
        style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", objectFit: "cover", zIndex: 0, pointerEvents: "none" }}
      />
      
      {/* Logo ASCME centralizada */}
      <div className="logo-container" >
        <img 
          src={getImageUrl('lo.png')} 
          alt=" Logo"
        />
      </div>

      {/* Logo ACHE no canto superior direito */}
      <div className="logo-ache-container">
        <img 
          src={getImageUrl('lo.png')} 
          alt=" Logo"
          
        />
      </div>

      {/* Texto central */}
      <div className="central-question">
        Quem leva mais vida para os pacientes?
      </div>

      <div className="photo-wall" style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        pointerEvents: "none"
      }}>
        {/* Foto bubbles com transições graduais */}
        {photoBubbles.map((bubble, index) => (
          <FloatingPhotoCircle
            key={bubble.person.id}
            id={bubble.person.id}
            photo_url={bubble.person.photo_url}
            name={bubble.person.name}
            field_1=""
            isNew={bubble.isNew}
            speed={Math.random() * 10 + 10} // Velocidade entre 15 e 45 pixels/segundo
            minDistance={2}
            movementBounds={getMovementBounds()}
            className={index % 2 === 0 ? "circle-distant" : "circle-close"}
            style={{ '--delay': index } as React.CSSProperties}
            initialSizePercentage={Math.random() * 10 + 10} // Tamanho inicial aleatório entre 10% e 20%
            shouldEnter={bubble.shouldEnter}
            shouldExit={bubble.shouldExit}
            onExitComplete={() => handlePhotoBubbleExitComplete(bubble.person.id)}
          />
        ))}
        
        {/* Balões ACHE flutuantes com transições graduais */}
        {acheBalloons.map((balloon, index) => (
          <FloatingAcheBalloon
            key={balloon.id}
            id={balloon.id}
            speed={balloon.speed}
            minDistance={3}
            sizePercentage={balloon.sizePercentage}
            movementBounds={getMovementBounds()}
            className={index % 2 === 0 ? "ache-balloon-distant" : "ache-balloon-close"}
            shouldEnter={balloon.shouldEnter}
            shouldExit={balloon.shouldExit}
            onExitComplete={() => handleBalloonExitComplete(balloon.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default AnimatedPhotoWall;